import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';

const FieldBrowser = ({ entityName, selectedFields = [], onFieldToggle }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState([]);

  useEffect(() => {
    if (entityName) {
      loadFields();
    }
  }, [entityName]);

  // Auto-expand all groups when fields are loaded
  useEffect(() => {
    if (fields.length > 0) {
      const allGroups = [...new Set(fields.map(f => f.group || 'General'))];
      setExpandedGroups(allGroups);
    }
  }, [fields]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/entity-metadata/${entityName}/fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load fields');
      }

      const data = await response.json();
      setFields(data.data || []);
    } catch (error) {
      console.error('Error loading fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const filteredFields = fields.filter(field =>
    field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group fields by category
  const groupedFields = filteredFields.reduce((acc, field) => {
    const group = field.group || 'General';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(field);
    return acc;
  }, {});

  const isFieldSelected = (fieldName) => {
    return selectedFields.some(f => f.name === fieldName || f === fieldName);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Available Fields
      </label>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
        />
      </div>

      {/* Fields List */}
      <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        {Object.keys(groupedFields).length === 0 ? (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
            No fields found
          </div>
        ) : (
          Object.entries(groupedFields).map(([groupName, groupFields]) => (
            <div key={groupName} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="font-medium text-sm text-gray-700 dark:text-gray-300">
                  {groupName} ({groupFields.length})
                </span>
                {expandedGroups.includes(groupName) ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {expandedGroups.includes(groupName) && (
                <div className="bg-gray-50 dark:bg-gray-800/50">
                  {groupFields.map(field => (
                    <label
                      key={field.name}
                      className="flex items-center gap-2 p-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isFieldSelected(field.name)}
                        onChange={() => onFieldToggle(field)}
                        className="rounded text-teal-600 focus:ring-teal-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {field.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {field.name} • {field.type}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FieldBrowser;

